import axios from 'axios'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import Background from '../../components/Background'
import Button from '../../components/Button.js'
import Input from '../../components/form/Input.js'
import Logo from '../../components/Logo'
import DialogPopup from '../../components/DialogPopup'
import OverlayContent from '../../components/OverlayContent'
import Bravo from '../../components/typography/Bravo'
import Body from '../../components/typography/Body'
import Delta from '../../components/typography/Delta'
import QuizContext from '../../context/QuizContext'
import useCurrentUser from '../../hooks/useCurrentUser'
import useToken from '../../hooks/useToken'
import Head from 'next/head'
import Link from 'next/link'
import Countdown from '../../components/Countdown'
import HubSpot from '~/components/HubSpot'
import WarningType from '~/components/WarningType'
import * as leagueColours from 'league_colours.js'
import PreviewInfo from '~/components/login/PreviewInfo'
import { checkPin } from '~/components/login/Utils'
import Checkbox from '~/components/form/Checkbox'

export default function Index(props) {
  const token = useToken()
  const router = useRouter()
  const currentUser = useCurrentUser()
  const [pin, setPin] = useState(router.query.pin ?? '')
  const [quizPreview, setQuizPreview] = useState(false)
  const [quizFull, setQuizFull] = useState(false)
  const [errors, setErrors] = useState(false)
  const [email, setEmail] = useState('')
  const [disableJoin, setDisableJoin] = useState(false)
  const [customTermsAccepted, setCustomTermsAccepted] = useState(false)

  const [quizStatus, setQuizStatus] = useState('not_started')

  const [startTime, setStartTime] = useState(false)

  //const { venue } = router.query
  const [venueName, setVenueName] = useState('')
  const [venueError, setVenueError] = useState(false)
  const [venueUuid, setVenueUuid] = useState(false)

  const { quizData, setQuizData } = useContext(QuizContext)
  const [cookies, setCookie, deleteCookie] = useCookies([
    'quiz_uuid',
    'public_user_uuid',
    'user_uuid',
    'venue_name',
    'venue_uuid',
    'pin'
  ])
  const user = useCurrentUser()

  const updateEmail = (evt) => {
    setEmail(evt.target.value)
  }

  const termsLabel = () => {
    if (quizData?.is_custom_terms) {
      return (
        <>
          {quizData?.custom_terms_text}{' '}
          <a
            className="underline"
            href={quizData?.custom_terms_link}
            target="_blank"
          >
            {quizData?.custom_terms_link_text}
          </a>
        </>
      )
    }
  }

  const termsLabelChangeHandler = (evt) => {
    if (errors) {
      setErrors({})
    }
    setCustomTermsAccepted(evt.target.checked)
  }

  const checkEmail = () => {
    axios
      .post(
        `${process.env.base_api}/auth/user-existence`,
        { email: email },
        { validateStatus: false }
      )
      .then(function (response) {
        response.data.errors ? setErrors(response.data.errors) : setErrors({})
        if (response.status === 404) {
          router.push({
            pathname: '/signup',
            query: { email: email, fromJoin: true }
          })
        } else if (response.status === 200) {
          router.push({
            pathname: '/login',
            query: { email: email, fromJoin: true }
          })
        }
      })
  }

  const addUserToQuiz = async () => {
    var data = {
      user_name: user.name,
      quiz_uuid: quizPreview.uuid,
      user_uuid: user.uuid
    }

    if (quizPreview.is_kblive === '1') {
      if (venueUuid) {
        data.venue_uuid = venueUuid
        data.host_name = venueName
      } else {
        data.venue_uuid = cookies.venue_uuid
        data.host_name = cookies.venue_name
      }
    }

    // A signed join token proves the logged-in account to the engine — quizzes
    // flagged as secure refuse joins without one
    if (token) {
      try {
        const tokenResponse = await axios.get(
          `${process.env.base_api}/quiz/${quizPreview.uuid}/join-token`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        data.join_token = tokenResponse.data.join_token
      } catch (error) {
        // The engine rejects the join if the quiz requires a token
      }
    }

    var config = {
      method: 'post',
      url: `${process.env.base_engine}/quiz-initialise-user.php`,
      data: data
    }

    axios(config)
      .then(function (response) {
        const { data } = response

        if (data.quiz_full) {
          setQuizFull(true)
          return
        }
        // The engine's response is canonical: a rejoin resumes the original
        // identity (same public_user_uuid and name) so the score carries on
        setCookie('user_name', data.user_name ?? user.name, { path: '/' })
        setCookie('public_user_uuid', data.public_user_uuid, { path: '/' })
        setCookie('user_uuid', data.user_uuid ?? user.uuid, { path: '/' })
        setCookie('quiz_uuid', data.quiz_uuid, { path: '/' })
        if (data.session) {
          setCookie('quiz_session', data.session, { path: '/' })
        }
        if (venueUuid) {
          setCookie('venue_uuid', venueUuid, { path: '/' })
        }
        if (venueName) {
          setCookie('venue_name', venueName, { path: '/' })
        }

        // setCookie('pin', data.pin, { path: '/' })
        router.push('/quiz')
      })
      .catch(function (error) {
        if (error.response?.status === 401) {
          setErrors({
            pin: [
              error.response.data?.error ??
                'You need to be logged in to join this quiz'
            ]
          })
        }
        setDisableJoin(false)
      })
  }

  const joinAsGuest = () => {
    router.push('join/guest')
  }

  const proceedToQuiz = () => {
    if (disableJoin) return
    if (venueError) return
    if (quizData?.is_custom_terms && !customTermsAccepted) {
      setErrors({ custom_terms: ['The terms and conditions must be accepted'] })
      return
    }
    setDisableJoin(true)

    setQuizData({
      ...quizData,
      ...quizPreview
    })
    setCookie('quiz_uuid', quizPreview.uuid, { path: '/' })
    addUserToQuiz()
  }

  useEffect(() => {
    if (router.query.pin) {
      setPin(router.query.pin)
      checkPin({
        pinCode: router.query.pin,
        cookies,
        setCookie,
        setQuizPreview,
        setStartTime,
        quizData,
        setQuizData,
        setErrors,
        router
      })
    }
  }, [router.query.pin])

  useEffect(() => {
    if (router.query.venue) {
      setVenueUuid(router.query.venue)
      setCookie('venue_uuid', router.query.venue, { path: '/' })
      axios
        .get(
          `${process.env.base_api}/search-host-name?host_uuid=${router.query.venue}`
        )
        .then(function (response) {
          const { data } = response
          setVenueName(data.host_name)
          setCookie('venue_name', data.host_name, { path: '/' })
        })
        .catch(function (err) {
          // Only treat a server response as an invalid venue — on a network
          // failure the venue uuid is still valid, we just couldn't resolve
          // its name (the report falls back to resolving it server-side)
          if (err.response) {
            setVenueError(true)
          }
        })
    }
  }, [router.query.venue])

  const submitPin = (e) => {
    e.preventDefault()

    checkPin({
      pinCode: pin,
      cookies,
      setCookie,
      setQuizPreview,
      setStartTime,
      quizData,
      setQuizData,
      setErrors,
      router
    })
  }
  const updatePin = (evt) => {
    setPin(evt.target.value)
  }

  return (
    <Background
      position="end"
      backgroundImage={quizPreview?.desktop_image}
      mobileBackgroundImage={quizPreview?.mobile_image}
      logo={quizPreview?.logo}
    >
      <Head>
        <title>Join a quiz | KwizzBit</title>
      </Head>
      <DialogPopup
        openModal={() => setQuizFull(true)}
        closeModal={() => setQuizFull(false)}
        isOpen={quizFull}
      >
        <Bravo className="pb-20">Quiz is full</Bravo>
        <Body>
          Sorry, the quiz is full as the game has reached its player limit.
        </Body>
        <Button
          className="mt-20 mb-10"
          onClick={() => router.push('/dashboard')}
        >
          Play another quiz
        </Button>
        <button onClick={() => setQuizFull(false)}>Close</button>
      </DialogPopup>

      <DialogPopup
        openModal={() => {}}
        closeModal={() => {}}
        isOpen={venueError}
      >
        <Bravo className="pb-20">Error</Bravo>
        <Body>
          Sorry, there was an error joining the quiz. Please try scanning the QR
          code again.
        </Body>
      </DialogPopup>

      {!router.query.pin && (
        <div className="container">
          <button
            className="flex items-center m-20 text-white"
            onClick={() => router.push('/dashboard')}
          >
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
              <circle cx="15" cy="15" r="14" stroke="white" strokeWidth="2" />
              <path
                d="M17.2566 9.41082C17.582 9.73626 17.582 10.2639 17.2566 10.5893L12.8458 15.0001L17.2566 19.4108C17.582 19.7362 17.582 20.2639 17.2566 20.5893C16.9312 20.9147 16.4035 20.9147 16.0781 20.5893L11.0781 15.5893C10.7526 15.2639 10.7526 14.7362 11.0781 14.4108L16.0781 9.41082C16.4035 9.08539 16.9312 9.08539 17.2566 9.41082Z"
                fill="white"
              />
            </svg>
            <span className="ml-5">Back</span>
          </button>
        </div>
      )}
      <div className="flex flex-col justify-end md:justify-center h-full flex-1">
        <OverlayContent>
          {quizPreview ? (
            <div>
              <div className="max-w-[250px] mx-auto text-center mb-20">
                {quizPreview.logo ? (
                  <Image
                    src={quizPreview?.logo ?? false}
                    width="100"
                    height="50"
                    layout="fixed"
                    className="object-contain"
                  />
                ) : (
                  <Logo />
                )}
              </div>
              <Bravo className="my-20">
                You've been invited to join the quiz
              </Bravo>
              <PreviewInfo data={quizPreview} />
              {venueName && (
                <div className="flex items-center">
                  <div>
                    <div className="mt-10">
                      <span className="font-base font-bold">Your venue:</span>
                      <span className="font-base ml-5 mt-10 ">{venueName}</span>
                    </div>
                  </div>
                </div>
              )}
              {quizPreview.league_id && (
                <>
                  <div className="flex items-center">
                    <div>
                      <div className="mt-10">
                        <span className="font-base font-bold">League:</span>
                        <span className="font-base ml-5 mt-10 ">
                          {quizPreview.league_name}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-neutral-800 bg-opacity-50 rounded-lg my-20 bg-orange-100 px-20 pt-20 pb-4">
                    <WarningType
                      title={quizPreview.league_name}
                      type="league"
                      iconColour={
                        leagueColours[quizPreview.league_colour]['hex']
                      }
                    >
                      This is a league quiz. Create or login to your account to
                      score points in this league. Guest scores will not be
                      recorded.
                    </WarningType>
                  </div>
                </>
              )}
              {!startTime && (
                <div className="mt-20">
                  {currentUser ? (
                    <div>
                      {quizData?.is_custom_terms && (
                        <Checkbox
                          label={termsLabel()}
                          onChange={termsLabelChangeHandler}
                          error={errors.custom_terms ?? ''}
                        />
                      )}
                      <Button
                        onClick={() => proceedToQuiz()}
                        disabled={disableJoin}
                        label="Join quiz"
                      />
                    </div>
                  ) : (
                    <div>
                      <Input
                        onChange={updateEmail}
                        error={errors.email ?? false}
                        type="email"
                        label="Email"
                        value={email}
                        placeholder="email"
                      />
                      <Button onClick={() => checkEmail()} label="Continue" />
                      {quizPreview.is_guest_play && (
                        <>
                          <hr className="my-20 border-transparent h-1 bg-neutral-300" />
                          <Button
                            onClick={() => joinAsGuest()}
                            theme="secondary"
                            label="Play as a guest"
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-center">
                <div className="max-w-[250px] mx-auto text-center mb-20">
                  {quizPreview.logo ? (
                    <Image
                      src={quizPreview?.logo ?? false}
                      width="100"
                      height="50"
                      layout="fixed"
                      className="object-contain"
                    />
                  ) : (
                    <Logo />
                  )}
                </div>
                <Bravo>Join quiz</Bravo>
              </div>
              {!startTime && (
                <>
                  <form className="mt-20" onSubmit={submitPin}>
                    <Input
                      type="text"
                      error={errors.pin}
                      onChange={updatePin}
                      label="Enter KwizzPIN"
                      value={pin}
                      placeholder="1234567"
                    />
                    <Button onClick={submitPin} label="Continue" />
                  </form>
                  <Link href="/login?fromJoin">
                    <a className="text-caption-fluid text-center mt-20 block">
                      Want to host a game? Login here.
                    </a>
                  </Link>
                </>
              )}
            </div>
          )}
          {startTime && (
            <div className="text-center border-t border-neutral-300 pt-10 mt-20">
              <Delta className="text-black -mb-20 mt-20 ">
                You can join the quiz 10 minutes before it starts
              </Delta>
              <Countdown
                date={startTime}
                dark={true}
                returnCompleted={() => setQuizStatus('not_started')}
              />
            </div>
          )}
        </OverlayContent>
      </div>
      <HubSpot page={'join/'} />
    </Background>
  )
}
